const TermsPage = () => {
  return (
    <div className="pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-6">
        <div className="space-y-8">
          {/* Header Section - matches landing page legal style */}
          <header className="text-center mb-12">
            <p className="text-sm font-medium uppercase tracking-widest text-crimson">Legal</p>
            <h1 className="mt-3 font-heading text-4xl font-bold text-foreground sm:text-5xl">
              Terms of Use
            </h1>
            <p className="mt-4 text-muted-foreground">
              These terms govern your use of our website, platform, and mystery generation services.
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              Last Updated: February 20, 2025
            </p>
          </header>

          <div className="prose dark:prose-invert max-w-none space-y-12">
            {/* Introduction */}
            <div className="space-y-6">
              <div className="rounded-xl border border-border bg-card p-6">
                <p className="text-muted-foreground text-lg leading-relaxed">
                  These Terms of Use ("Terms") govern your access to and use of
                  ColdCaseGameKits.com and its services. Please read these Terms
                  carefully before using our services.
                </p>
              </div>

              {/* Quick Reference Box - matches landing card style */}
              <div className="rounded-xl border border-border bg-card p-6">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-4">Quick Reference</h3>
                <div className="grid gap-3 text-sm text-muted-foreground">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <span className="font-medium">Company:</span>
                  <span className="md:col-span-2">DGF Digital Inc.</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <span className="font-medium">Website:</span>
                  <span className="md:col-span-2">ColdCaseGameKits.com</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <span className="font-medium">Email:</span>
                  <span className="md:col-span-2">contact@dgfdigital.com</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <span className="font-medium">Address:</span>
                  <span className="md:col-span-2">
                    9-2020 Lanthier Drive Suite 111, Orleans, ON K4A 3V4
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Table of Contents - matches landing card style */}
          <div className="rounded-xl border border-border bg-card p-6">
            <h2 className="font-heading text-xl font-semibold text-foreground mb-4">Table of Contents</h2>
            <nav className="grid gap-2 text-sm">
              <a href="#acceptance" className="text-crimson hover:underline">1. Acceptance of Terms</a>
              <a href="#definitions" className="text-crimson hover:underline">2. Definitions</a>
              <a href="#account" className="text-crimson hover:underline">3. Account Registration and Security</a>
              <a href="#license" className="text-crimson hover:underline">4. License Grant and Restrictions</a>
              <a href="#prohibited" className="text-crimson hover:underline">5. Prohibited Activities</a>
              <a href="#ip" className="text-crimson hover:underline">6. Intellectual Property Rights</a>
              <a href="#ugc" className="text-crimson hover:underline">7. User-Generated Content</a>
              <a href="#payment" className="text-crimson hover:underline">8. Payment Terms</a>
              <a href="#disclaimer" className="text-crimson hover:underline">9. Disclaimer of Warranties</a>
              <a href="#liability" className="text-crimson hover:underline">10. Limitation of Liability</a>
              <a href="#indemnification" className="text-crimson hover:underline">11. Indemnification</a>
              <a href="#disputes" className="text-crimson hover:underline">12. Dispute Resolution</a>
              <a href="#modifications" className="text-crimson hover:underline">13. Modifications to Terms</a>
              <a href="#termination" className="text-crimson hover:underline">14. Termination</a>
              <a href="#contact" className="text-crimson hover:underline">15. Contact Information</a>
            </nav>
          </div>

          {/* Main Content Sections */}
          <section id="acceptance" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">1. Acceptance of Terms</h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p>
                By accessing or using ColdCaseGameKits.com ("the Site"), you
                agree to be bound by these Terms and our Privacy Policy. If you
                disagree with any part of these terms, you may not access the
                Site or use our services.
              </p>
            </div>
          </section>

          <section id="definitions" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">2. Definitions</h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  "Company," "we," "us," or "our" refers to DGF Digital Inc.
                </li>
                <li>
                  "Site" refers to ColdCaseGameKits.com and its associated
                  services
                </li>
                <li>
                  "User," "you," or "your" refers to any individual or entity
                  using the Site
                </li>
                <li>
                  "Content" refers to all materials, game kits, and resources
                  available on the Site
                </li>
                <li>
                  "Games and Game Kits" refers to our digital mystery game products and
                  related materials
                </li>
              </ul>
            </div>
          </section>

          <section id="account" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">
                3. Account Registration and Security
              </h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <ul className="list-disc pl-5 space-y-2">
                <li>You must be at least 13 years old to create an account</li>
                <li>
                  You must provide accurate, complete, and current information
                </li>
                <li>
                  You are responsible for maintaining the security of your
                  account
                </li>
                <li>
                  You must notify us immediately of any unauthorized access
                </li>
                <li>
                  We reserve the right to suspend or terminate accounts at our
                  discretion
                </li>
              </ul>
            </div>
          </section>

          <section id="license" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">
                4. License Grant and Restrictions
              </h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p className="mb-4">
                We grant you a limited, non-exclusive, non-transferable, and
                revocable license to:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  Access and use the Site for personal, non-commercial use
                </li>
                <li>
                  Download and use our Game Kits as intended for entertainment
                  purposes
                </li>
                <li>Print materials for personal use in playing our Game Kits</li>
              </ul>
            </div>
          </section>

          <section id="prohibited" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">5. Prohibited Activities</h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p className="mb-4">You may not:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Redistribute, sell, or sublicense our Game Kits or Content</li>
                <li>Modify, reverse engineer, or create derivative works</li>
                <li>Use the Site for any illegal or unauthorized purpose</li>
                <li>Interfere with or disrupt the Site's operation</li>
                <li>
                  Attempt to gain unauthorized access to any part of the Site
                </li>
                <li>Share account credentials or transfer account access</li>
              </ul>
            </div>
          </section>

          <section id="ip" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">
                6. Intellectual Property Rights
              </h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p>
                All Content, including but not limited to game kits, text, graphics,
                logos, and software, is the property of DGF Digital Inc. and is
                protected by copyright, trademark, and other intellectual
                property laws.
              </p>
            </div>
          </section>

          <section id="ugc" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">7. User-Generated Content</h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  We (DGF Digital Inc.) retain ownership of any content you submit to the Site
                </li>
                <li>
                  You grant us a worldwide, non-exclusive license to use,
                  modify, and display your content
                </li>
                <li>
                  You are responsible for ensuring your content doesn't violate
                  any rights
                </li>
                <li>We may remove any content at our discretion</li>
              </ul>
            </div>
          </section>

          <section id="payment" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">8. Payment Terms</h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <ul className="list-disc pl-5 space-y-2">
                <li>All prices are in USD unless otherwise stated</li>
                <li>
                  Payments are processed securely through our payment processor (FastSpring)
                </li>
                <li>We reserve the right to modify prices at any time</li>
                <li>Refunds are subject to our Refund Policy</li>
              </ul>
            </div>
          </section>

          <section id="disclaimer" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">
                9. Disclaimer of Warranties
              </h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p>
                THE SITE AND ALL CONTENT ARE PROVIDED "AS IS" WITHOUT WARRANTY
                OF ANY KIND. WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED,
                INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
                MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
                NON-INFRINGEMENT.
              </p>
            </div>
          </section>

          <section id="liability" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">
                10. Limitation of Liability
              </h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p>
                IN NO EVENT SHALL DGF DIGITAL INC. BE LIABLE FOR ANY INDIRECT,
                INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING
                OUT OF OR RELATED TO YOUR USE OF THE SITE OR GAME KITS.
              </p>
            </div>
          </section>

          <section id="indemnification" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">11. Indemnification</h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p>
                You agree to indemnify and hold harmless DGF Digital Inc., its
                officers, directors, employees, and agents from any claims,
                damages, losses, liabilities, and expenses arising from your use
                of the Site or violation of these Terms.
              </p>
            </div>
          </section>

          <section id="disputes" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">12. Dispute Resolution</h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p>
                Any dispute arising from these Terms shall be governed by the
                laws of Ontario, Canada. You agree to submit to the exclusive
                jurisdiction of the courts located in Ontario for resolution of
                any disputes.
              </p>
            </div>
          </section>

          <section id="modifications" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">13. Modifications to Terms</h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p>
                We reserve the right to modify these Terms at any time. Changes
                will be effective immediately upon posting to the Site. Your
                continued use of the Site constitutes acceptance of the modified
                Terms.
              </p>
            </div>
          </section>

          <section id="termination" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">14. Termination</h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p>
                We may terminate or suspend your access to the Site immediately,
                without prior notice or liability, for any reason, including
                breach of these Terms.
              </p>
            </div>
          </section>

          <section id="contact" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">15. Contact Information</h2>
            </div>
            <div className="rounded-xl border border-crimson/20 bg-crimson/5 p-6">
              <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
                Questions about these Terms?
              </h3>
              <address className="not-italic space-y-2 text-muted-foreground">
                <p className="font-medium text-foreground">DGF Digital Inc.</p>
                <p>9-2020 Lanthier Drive Suite 111</p>
                <p>Orleans, ON K4A 3V4</p>
                <p className="mt-4">
                  <span className="font-medium text-foreground">Email: </span>
                  <a
                    href="mailto:contact@dgfdigital.com"
                    className="text-crimson hover:underline"
                  >
                    contact@dgfdigital.com
                  </a>
                </p>
              </address>
            </div>
          </section>

          {/* Footer - matches landing legal footer */}
          <div className="border-t border-border pt-8 mt-12 text-center text-sm text-muted-foreground space-y-2">
            <p>Effective Date: January 1, 2025</p>
            <p>Last Updated: February 20, 2025</p>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
